import React, { useState } from "react";
import { EarningsEvent } from "./types";
import { formatRevenue, formatTime } from "./utils";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";

import {
  FiClock,
  FiTrendingUp,
  FiDollarSign,
  FiSun,
  FiMoon,
} from "react-icons/fi";
import AnimatedButton from "../common/AnimatedButton";
import { ChevronRight } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useTranslation } from "react-i18next";

interface EarningsCardProps {
  event: EarningsEvent;
  logoUrl?: string;
  onTrade?: (ticker: string) => void;
  CTALoading?: boolean;
}

const IMPACT_CONFIG = {
  high: {
    label: "High",
    dotClass: "bg-[#10B981]",
    glowClass: "shadow-[0_0_12px_rgba(16,185,129,0.5)]",
    textClass: "text-[#10B981]",
  },
  medium: {
    label: "Medium",
    dotClass: "bg-amber-500",
    glowClass: "shadow-[0_0_12px_rgba(245,158,11,0.5)]",
    textClass: "text-amber-500",
  },
  low: {
    label: "Low",
    dotClass: "bg-rose-500",
    glowClass: "shadow-[0_0_12px_rgba(244,63,94,0.5)]",
    textClass: "text-rose-500",
  },
};
function getImpact(importance: number) {
  if (importance >= 4) return IMPACT_CONFIG.high;
  if (importance >= 3) return IMPACT_CONFIG.medium;
  return IMPACT_CONFIG.low;
}

function formatEPS(value: number | undefined | null) {
  return value !== null && value !== undefined ? value.toFixed(2) : "NA";
}

export const EarningsCard: React.FC<EarningsCardProps> = ({
  event,
  logoUrl,
  onTrade,
  CTALoading,
}) => {
  const { t } = useTranslation("build-page");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const impact = getImpact(event.importance);

  const formattedTime = formatTime(event.time, t);

  const estRev = formatRevenue(event.estimated_revenue, t, event.currency);
  const prevRev = formatRevenue(event.previous_revenue, t, event.currency);

  const epsEst = formatEPS(event.estimated_eps);
  const epsPrev = formatEPS(event.previous_eps);

  const getMetricColor = (
    est: number | null | undefined,
    prev: number | null | undefined,
  ) => {
    if (
      est === null ||
      est === undefined ||
      prev === null ||
      prev === undefined ||
      est === prev
    )
      return "text-white";
    return est > prev
      ? "text-[--marketplace-green]"
      : "text-[--marketplace-red]";
  };

  const epsColor = getMetricColor(event.estimated_eps, event.previous_eps);
  const revColor = getMetricColor(
    event.estimated_revenue,
    event.previous_revenue,
  );

  return (
    <div
      className={cn(
        "group relative select-none bg-blue-950/[0.05] border border-blue-500/[0.08] rounded-2xl p-4 flex flex-col gap-4",
        "transition-all duration-500 hover:bg-blue-900/[0.08] hover:border-blue-400/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] hover:-translate-y-1 antialiased",
      )}
    >
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex flex-wrap gap-4 min-w-0">
          {/* Logo / Ticker Badge */}
          {logoUrl ? (
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white/5 flex items-center justify-center shrink-0 border border-white/10  shadow-inner group-hover:border-white/20 transition-colors">
              <img
                src={logoUrl}
                alt={event.ticker}
                className="w-full h-full object-contain filter brightness-110"
              />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-[#2D343F] to-[#1A202C] text-white/90 text-xs font-black px-3 py-2 rounded-xl self-start shrink-0 h-fit flex items-center justify-center min-w-[50px] border border-white/5 shadow-lg">
              {event.ticker}
            </div>
          )}

          <div className="flex flex-col gap-2 min-w-0 justify-center">
            <h3 className="text-white text-lg sm:text-xl font-bold truncate leading-none tracking-tight group-hover:text-muted-foreground transition-colors ">
              {event.company_name}
            </h3>
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "w-1.5 h-1.5 rounded-full",
                  impact.dotClass,
                  impact.glowClass,
                )}
              />
              <span
                className={cn(
                  "text-[8px] font-bold  tracking-[0.2em]",
                  impact.textClass,
                )}
              >
                {t("{{label}} Impact", { label: t(impact.label) })}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2.5 shrink-0 ml-auto sm:ml-0">
          <div className="flex gap-2 items-center">
            <Badge
              variant="secondary"
              className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 text-[9px] font-bold tracking-widest px-2.5 py-1 border border-blue-500/20 rounded-full "
            >
              {event.fiscal_period} {event.fiscal_year}
            </Badge>
            <div className="flex items-center justify-center">
              {event.abbr_time?.toLowerCase() === "bmo" ? (
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger
                    asChild
                    onMouseEnter={() => setIsPopoverOpen(true)}
                    onMouseLeave={() => setIsPopoverOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center border border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.2)] cursor-pointer">
                      <FiSun className="w-4 h-4 text-amber-500" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit bg-[#060B18] border-white/10 text-white text-[10px] font-bold tracking-wider px-3 py-1.5 shadow-2xl  pointer-events-none">
                    {t("Before Market Open")}
                  </PopoverContent>
                </Popover>
              ) : event.abbr_time?.toLowerCase() === "amc" ? (
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                  <PopoverTrigger
                    asChild
                    onMouseEnter={() => setIsPopoverOpen(true)}
                    onMouseLeave={() => setIsPopoverOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.2)] cursor-pointer">
                      <FiMoon className="w-4 h-4 text-blue-400" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit bg-[#060B18] border-white/10 text-white text-[10px] font-bold tracking-wider px-3 py-1.5 shadow-2xl  pointer-events-none">
                    {t("After Market Close")}
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="flex items-center justify-center gap-2 text-white/20 group-hover:text-white/40 transition-colors">
                  <FiClock className="w-3.5 h-3.5" />
                  <div className="h-auto">
                    <p className="text-[14px] font-bold">{formattedTime}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-1 border-t border-white/[0.03]">
        <div className="flex gap-6">
          {/* EPS */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 text-muted-foreground text-[11px] font-bold  tracking-[0.2em]">
              <FiTrendingUp className="w-3 h-3 text-muted-foreground" />
              <span>{t("EPS (EST/PRV)")}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span
                className={cn(
                  "text-xl font-bold tabular-nums tracking-tight",
                  epsColor,
                )}
              >
                {epsEst}
              </span>
              <span className="text-muted-foreground text-base font-medium tabular-nums">
                / {epsPrev}
              </span>
            </div>
          </div>
        </div>

        {/* Trade CTA */}
        <div className="w-full flex justify-end">
          <Button
            loading={CTALoading}
            disabled={CTALoading}
            size={"sm"}
            onClick={() => onTrade?.(event.ticker)}
          >
            <div className="flex items-center flex-row gap-2 px-1 py-0.5 font-bold ">
              <div>{t("Trade Now")}</div>
              <ChevronRight className="w-3.5 h-3.5" />
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};
